import React, { useState } from 'react';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

// ✅ ADDED: Page-level meta via react-helmet (or just document.title if no helmet installed)
// If you have react-helmet: wrap return in <Helmet> with title + meta description

const Works = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // ✅ REMOVED: copyToClipboard function was defined but never used — dead code removed

  // ✅ IMPROVED: FAQs updated with better answers, fixed "2024" to "2026",
  //    added new questions targeting zero-click queries from Search Console
  const faqs = [
    {
      question: 'Can Bootstrap be used with Tailwind CSS?',
      answer: 'You can load both in the same project, but it is not recommended. Bootstrap and Tailwind both include CSS resets that conflict with each other, and their class names can overlap. The better approach is to fully migrate using our Bootstrap to Tailwind converter, then remove Bootstrap from your project.'
    },
    {
      question: 'Is Tailwind CSS better than Bootstrap?',
      answer: 'It depends on your use case. Tailwind is a utility-first framework — it gives you low-level classes to build completely custom designs without fighting pre-baked styles. Bootstrap is component-based and faster for prototyping with pre-built UI. For long-term projects where design consistency and bundle size matter, Tailwind is generally preferred.'
    },
    {
      question: 'How do I convert Bootstrap to Tailwind CSS?',
      answer: 'Use our free online Bootstrap to Tailwind converter. Paste your Bootstrap HTML into the input field, click Convert, and the tool replaces every Bootstrap class with its Tailwind CSS equivalent. Copy the output and paste it into your project.'
    },
    {
      question: 'Can I convert Tailwind CSS back to Bootstrap?',
      answer: 'Yes. Our tool supports both directions. Use the Tailwind → Bootstrap toggle on the converter page to convert Tailwind utility classes back to Bootstrap class names.'
    },
    {
      question: 'Does this work as a CSS to Tailwind converter?',
      answer: 'Our tool converts Bootstrap class names inside HTML markup to Tailwind equivalents. If your HTML already uses Bootstrap classes, paste it directly. The converter processes the full file and replaces all recognizable Bootstrap classes.'
    },
    {
      question: 'Does this work as an HTML to Tailwind converter?',
      answer: 'Yes — paste any full HTML file that contains Bootstrap class names. The converter scans every element recursively and replaces Bootstrap classes with Tailwind equivalents throughout the entire document.'
    },
    {
      question: 'What is the difference between Bootstrap 5 and Tailwind CSS?',
      answer: 'Bootstrap 5 ships with pre-built components (navbars, modals, cards) that are ready to use but harder to customize. Tailwind CSS is utility-first — no default components, but you have full control over every style. Tailwind production builds are also significantly smaller (~5–15KB purged vs Bootstrap\'s ~150KB minified).'
    },
    {
      question: 'Is Tailwind CSS faster to develop with than writing plain CSS?',
      answer: 'Yes, for most developers. Tailwind eliminates context switching between HTML and CSS files. You style directly in your markup with utility classes, and the consistent spacing/color scale means fewer design decisions per component.'
    },
    {
      question: 'Is Bootstrap still worth using in 2026?',
      answer: 'Bootstrap is still widely used, especially for internal tools and admin dashboards where speed matters more than custom design. But for new consumer-facing projects, Tailwind CSS is increasingly the default choice due to better performance, smaller bundles, and more design flexibility.'
    },
    {
      question: 'How do I migrate a large project from Bootstrap to Tailwind?',
      answer: 'Start by converting page by page using our tool. Paste each template\'s HTML, convert it, test the output, and gradually remove Bootstrap from your dependencies. You can run both frameworks side-by-side temporarily during migration. Once all pages are converted, remove Bootstrap entirely from your package.json.'
    },
  ];

  return (
    <>
      <Navbar />

      {/* ✅ ADDED: Breadcrumb navigation for SEO */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="max-w-6xl mx-auto">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm text-gray-500">
              <li><Link to="/" className="hover:text-blue-600">Home</Link></li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-medium">How to Convert Bootstrap to Tailwind CSS</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="bg-gray-50 py-8">
        <div className="max-w-6xl container mx-auto px-4">

          {/* ✅ IMPROVED: H1 is more specific and keyword-rich */}
          <h1 className="text-5xl font-bold text-center mb-4">
            How to Convert Bootstrap to Tailwind CSS
          </h1>
          {/* ✅ ADDED: Subtitle targets secondary queries */}
          <p className="text-center text-gray-500 mb-12 max-w-2xl mx-auto">
            A step-by-step guide to using our free <strong>Bootstrap to Tailwind converter</strong>. 
            Also covers <strong>Tailwind to Bootstrap</strong> and <strong>HTML to Tailwind</strong> conversion.
          </p>

          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-4">What Is This Tool?</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our free <strong>Bootstrap to Tailwind CSS converter</strong> lets you paste any Bootstrap HTML and instantly get the equivalent Tailwind CSS classes back. It works as a <strong>Bootstrap migration tool</strong>, a <strong>CSS to Tailwind converter</strong>, and a <strong>Tailwind to Bootstrap converter</strong> — all in one. No signup, no install, no config required.
            </p>
          </section>

          {/* Step-by-Step Guide */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6">Step-by-Step: Bootstrap to Tailwind Migration</h2>
            <div className="space-y-6">
              {[
                {
                  step: "Step 1",
                  title: "Paste your Bootstrap HTML",
                  desc: "Copy your existing Bootstrap HTML and paste it into the left input panel on the converter. Works with Bootstrap 3, 4, and 5.",
                  example: null,
                },
                {
                  step: "Step 2",
                  title: "Choose conversion direction",
                  desc: "Select Bootstrap → Tailwind to migrate to Tailwind CSS, or Tailwind → Bootstrap to go the other way. The toggle is at the top of the converter.",
                  example: null,
                },
                {
                  step: "Step 3",
                  title: 'Click "Convert"',
                  desc: "Hit the Convert button. The tool recursively processes every element in your HTML and replaces Bootstrap class names with their Tailwind CSS equivalents.",
                  example: null,
                },
                {
                  step: "Step 4",
                  title: "Review the output",
                  desc: 'Classes that could not be automatically converted are marked with /* class not found */ so you can spot them and handle them manually.',
                  example: null,
                },
                {
                  step: "Step 5",
                  title: "Copy and use",
                  desc: "Click Copy Code to copy the converted Tailwind CSS to your clipboard. Paste it into your project and remove Bootstrap from your dependencies.",
                  example: null,
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md border-l-4 border-teal-500">
                  <div className="flex-shrink-0 bg-teal-600 text-white text-xs font-bold px-2 py-1 rounded">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/"
                className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg shadow-lg hover:bg-teal-700 transition duration-300 font-semibold"
              >
                Go to Converter →
              </Link>
            </div>
          </section>

          {/* Visual Examples */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-2">Visual Examples</h2>
            <p className="text-gray-600 mb-6">See how Bootstrap classes map to Tailwind CSS equivalents</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="border p-4 bg-white rounded-lg shadow-lg">
                <h3 className="text-xl font-medium mb-1 text-purple-700">Bootstrap Input</h3>
                <p className="text-xs text-gray-500 mb-3">Bootstrap 4/5 class names</p>
                <pre className="bg-gray-100 p-4 rounded overflow-x-auto text-sm"><code>{`<div class="container">
  <div class="row">
    <div class="col">
      <h1>Hello, world!</h1>
    </div>
  </div>
</div>`}</code></pre>
              </div>
              <div className="border p-4 bg-white rounded-lg shadow-lg">
                <h3 className="text-xl font-medium mb-1 text-teal-700">Tailwind CSS Output</h3>
                <p className="text-xs text-gray-500 mb-3">Converted Tailwind utility classes</p>
                <pre className="bg-gray-100 p-4 rounded overflow-x-auto text-sm"><code>{`<div class="container mx-auto">
  <div class="flex flex-wrap">
    <div class="flex-1 p-4">
      <h1 class="text-2xl font-bold">
        Hello, world!
      </h1>
    </div>
  </div>
</div>`}</code></pre>
              </div>
            </div>

            {/* ✅ ADDED: Second example targeting "btn" class conversion — common query */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
              <div className="border p-4 bg-white rounded-lg shadow-lg">
                <h3 className="text-xl font-medium mb-1 text-purple-700">Bootstrap Buttons</h3>
                <p className="text-xs text-gray-500 mb-3">Bootstrap button classes</p>
                <pre className="bg-gray-100 p-4 rounded overflow-x-auto text-sm"><code>{`<button class="btn btn-primary">
  Primary
</button>
<button class="btn btn-secondary">
  Secondary
</button>
<button class="btn btn-danger btn-sm">
  Delete
</button>`}</code></pre>
              </div>
              <div className="border p-4 bg-white rounded-lg shadow-lg">
                <h3 className="text-xl font-medium mb-1 text-teal-700">Tailwind CSS Buttons</h3>
                <p className="text-xs text-gray-500 mb-3">Converted Tailwind classes</p>
                <pre className="bg-gray-100 p-4 rounded overflow-x-auto text-sm"><code>{`<button class="bg-blue-600 text-white 
  px-4 py-2 rounded hover:bg-blue-700">
  Primary
</button>
<button class="bg-gray-500 text-white 
  px-4 py-2 rounded hover:bg-gray-600">
  Secondary
</button>
<button class="bg-red-600 text-white 
  px-2 py-1 text-sm rounded">
  Delete
</button>`}</code></pre>
              </div>
            </div>
          </section>

          {/* ✅ NEW: Tips section — adds content depth, targets "migrate from bootstrap to tailwind" */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6">Tips for Bootstrap to Tailwind Migration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: "Convert page by page",
                  desc: "For large projects, convert one template at a time. Test each page after conversion before moving to the next."
                },
                {
                  title: "Run both frameworks temporarily",
                  desc: "You can load Bootstrap and Tailwind side-by-side during migration. Remove Bootstrap once all pages are converted."
                },
                {
                  title: "Watch for /* not found */ comments",
                  desc: "The converter flags classes it couldn't map. Search your output for these comments and handle them manually."
                },
                {
                  title: "Replace Bootstrap JS components separately",
                  desc: "Bootstrap modals, dropdowns, and tooltips use JavaScript. Replace those with Headless UI, Flowbite, or custom Tailwind components."
                },
                {
                  title: "Purge Bootstrap from package.json",
                  desc: "Once migration is complete, remove bootstrap and @popperjs/core from your dependencies to shrink your bundle."
                },
                {
                  title: "Use Tailwind's config for your design system",
                  desc: "Move your Bootstrap overrides (custom colors, fonts, breakpoints) into tailwind.config.js to keep design tokens consistent."
                },
              ].map((tip, i) => (
                <div key={i} className="bg-white p-5 rounded-lg shadow-md border border-gray-100">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">💡 {tip.title}</h3>
                  <p className="text-sm text-gray-600">{tip.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQs — ✅ IMPROVED: accordion with chevron icons, better answers, more questions */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6">
              Frequently Asked Questions — Bootstrap to Tailwind
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
                  <button
                    className="w-full text-left px-6 py-4 flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-teal-500"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={activeIndex === index}
                  >
                    <h3 className="text-base font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    {activeIndex === index
                      ? <FaChevronUp className="text-teal-600 flex-shrink-0" />
                      : <FaChevronDown className="text-gray-400 flex-shrink-0" />
                    }
                  </button>
                  {activeIndex === index && (
                    <div className="px-6 pb-4 mt-3">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-teal-600 to-blue-600 rounded-xl p-8 text-center text-white mb-12">
            <h2 className="text-2xl font-bold mb-2">Ready to migrate your project?</h2>
            <p className="text-teal-100 mb-6">Free, instant, no signup. Paste your Bootstrap HTML and get Tailwind CSS in seconds.</p>
            <Link
              to="/"
              className="inline-block bg-white text-teal-700 font-bold px-8 py-3 rounded-lg hover:bg-gray-100 transition duration-300"
            >
              Open Bootstrap to Tailwind Converter →
            </Link>
          </section>

        </div>
      </div>
      <Footer />
    </>
  );
};

export default Works;